'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { type ColorMode, useThemeStore } from '@/stores/theme';
import { COLOR_MODES, getColorModeOptionIcon } from '@/utils/theme-mode';

export type ThemePickerLabels = {
  title: string;
  prompt: string;
  light: string;
  dark: string;
  system: string;
  cancel: string;
  save: string;
};

const defaultLabels: ThemePickerLabels = {
  title: 'Design',
  prompt: 'Wähle dein bevorzugtes Design:',
  light: 'Hell',
  dark: 'Dunkel',
  system: 'System',
  cancel: 'Abbrechen',
  save: 'Speichern',
};

type ThemePickerDialogProps = {
  open: boolean;
  onClose: () => void;
  labels?: Partial<ThemePickerLabels>;
};

function getModeLabel(mode: ColorMode, labels: ThemePickerLabels): string {
  switch (mode) {
    case 'light':
      return labels.light;
    case 'dark':
      return labels.dark;
    case 'system':
      return labels.system;
  }
}

export default function ThemePickerDialog({
  open,
  onClose,
  labels: labelOverrides,
}: ThemePickerDialogProps) {
  const labels = { ...defaultLabels, ...labelOverrides };
  const { colorMode, setColorMode } = useThemeStore();
  const [selectedMode, setSelectedMode] = useState<ColorMode>(colorMode);

  useEffect(() => {
    if (open) {
      setSelectedMode(colorMode);
    }
  }, [open, colorMode]);

  const handleSave = () => {
    setColorMode(selectedMode);
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle component="div" sx={{ m: 0, p: 2, position: 'relative' }}>
        <Typography variant="h6">{labels.title}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          sx={{
            color: (theme) => theme.palette.grey[500],
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          {labels.prompt}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {COLOR_MODES.map((mode) => {
            const isSelected = selectedMode === mode;

            return (
              <Box
                key={mode}
                component="button"
                onClick={() => setSelectedMode(mode)}
                sx={{
                  alignItems: 'center',
                  background: 'transparent',
                  border: 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  color: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1.5,
                  p: 2,
                  textAlign: 'left',
                  width: '100%',
                }}
                type="button">
                {getColorModeOptionIcon(mode)}
                <Typography sx={{ flex: 1, fontWeight: 500 }}>
                  {getModeLabel(mode, labels)}
                </Typography>
                <Radio checked={isSelected} size="small" sx={{ p: 0 }} />
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2, pt: 0 }}>
        <Button fullWidth onClick={onClose} variant="outlined">
          {labels.cancel}
        </Button>
        <Button fullWidth onClick={handleSave} variant="contained">
          {labels.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
